'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { useDriverLicenseList, deleteDriverLicense } from '../../../stores/driverLicenseStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import MasterDriverLicenseAddEdit from './MasterDriverLicenseAddEdit';
import { normalizeSearchFieldsConfig } from '../../../utils/searchHelper';
import { fleetApi } from '../../../api/axiosClient';
import { useLanguage } from '../../../utils/i18n';
import FilterBuilder from '../../../utils/filterBuilder';

function DriverLicensePageContent() {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { t } = useLanguage();

  const notify = useNotifier();
  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (!stored) return;
      const match = companyOptionsNormalized.find((o) => String(o.value) === String(stored));
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: String(stored), label: String(stored) }];

      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyFilterColumns = useMemo(() => {
    let storedCompanyId = null;
    try {
      if (typeof window !== 'undefined') storedCompanyId = localStorage.getItem('companyId');
    } catch {
      storedCompanyId = null;
    }

    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => String(s.value));
      if (values.length === 1) {
        return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] }];
      } else {
        return [{ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') }];
      }
    }

    if (storedCompanyId && String(storedCompanyId) !== 'all') {
      return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) }];
    }

    return [];
  }, [selectedCompanies]);

  const [searchField, setSearchField] = useState('license_name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'license_name', label: t('driverLicense.licenseName', 'License Name') },
      { value: 'threshold', label: t('driverLicense.thresholdWarning', 'Threshold Warning') },
      { value: 'threshold_danger', label: t('driverLicense.thresholdDanger', 'Threshold Danger') },
      { value: 'value_periode_notification', label: t('driverLicense.notifPeriode', 'Periode Notif') }
    ],
    [t]
  );

  const joinDefs = useMemo(
    () => ({
      company: { name: 'company', column_results: ['name'] }
    }),
    []
  );

  const searchFieldsConfig = useMemo(() => normalizeSearchFieldsConfig(searchFieldOptions), [searchFieldOptions]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchRaw = searchField === 'all' ? '' : searchValue;

  const {
    data: driverLicenseFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useDriverLicenseList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchRaw, filterCompanyIds);

  const [localDriverLicense, setLocalDriverLicense] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const rows = searchField === 'all' ? localDriverLicense : driverLicenseFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();
    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        const fb = new FilterBuilder().setPaging(start, length).addJoin('company', ['name']).setOrder(['license_name'], true);

        if (filterCompanyIds) {
          if (filterCompanyIds.includes(',')) {
            fb.in('company_id', filterCompanyIds);
          } else {
            fb.equal('company_id', filterCompanyIds);
          }
        } else {
          fb.filterByCompany();
        }

        const payload = fb.build();

        if (searchValue) {
          payload.filters = [
            { name: 'license_name', logic_operator: 'like', value: searchValue },
            { name: 'threshold', logic_operator: 'like', value: searchValue },
            { name: 'threshold_danger', logic_operator: 'like', value: searchValue },
            { name: 'value_periode_notification', logic_operator: 'like', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchRaw }
          ];
        }

        const res = await fleetApi.post(`/driver-license/search?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`, payload, {
          signal: controller.signal
        });

        const rawData = res?.data;
        // Tangani jika API melempar struktur { data: { data: [...] } } (double object)
        const fetchedRows = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : [];
        const total = rawData?.totalRecord ?? rawData?.dataSize ?? fetchedRows.length;

        if (!controller.signal.aborted) {
          setLocalDriverLicense(fetchedRows);
          setLocalTotal(total);
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error('driver-license search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, companyFilterColumns, searchFieldsConfig, joinDefs, filterCompanyIds, searchRaw]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const fb = new FilterBuilder().setPaging(0, length).addJoin('company', ['name']).setOrder(['license_name'], true);

      if (filterCompanyIds) {
        if (filterCompanyIds.includes(',')) {
          fb.in('company_id', filterCompanyIds);
        } else {
          fb.equal('company_id', filterCompanyIds);
        }
      } else {
        fb.filterByCompany();
      }

      const payload = fb.build();

      if (searchValue) {
        payload.filters = [
          { name: 'license_name', logic_operator: 'like', value: searchValue },
          { name: 'threshold', logic_operator: 'like', value: searchValue },
          { name: 'threshold_danger', logic_operator: 'like', value: searchValue },
          { name: 'value_periode_notification', logic_operator: 'like', value: searchValue },
          { name: 'name', table_name: 'm_company', value: searchRaw }
        ];
      }

      const res = await fleetApi.post(`/driver-license/search?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`, payload);
      const rawData = res?.data;
      const fetchedRows = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : [];
      return fetchedRows;
    } catch (err) {
      console.error('driver-license export all error', err);
      return [];
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw]);

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch (e) {
      console.log(e);
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteDriverLicense(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.log(err);
      notify({ message: t('driverLicense.notifFailed', 'Failed to delete Driver License'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1.2,
      renderCell: (params) => params.row?.join?.company_name || '-'
    },
    {
      field: 'license_name',
      headerName: t('driverLicense.licenseName', 'License Name'),
      flex: 1
    },
    {
      field: 'threshold',
      headerName: t('driverLicense.thresholdWarning', 'Threshold Warning'),
      flex: 1,
      valueGetter: (value, params) => `${params.threshold ?? 0} days`
    },
    {
      field: 'threshold_danger',
      headerName: t('driverLicense.thresholdDanger', 'Threshold Danger'),
      flex: 0.8,
      valueGetter: (value, params) => `${params.threshold_danger ?? 0} days`
    },
    {
      field: 'value_periode_notification',
      headerName: t('driverLicense.notifPeriode', 'Notif Periode'),
      flex: 1,
      valueGetter: (v, params) => {
        const value = params.value_periode_notification ?? '';
        const periode = params.periode_notification ?? '';
        return `${value} ${periode}`.trim() || '-';
      }
    },
    ...(typeof window !== 'undefined' &&
    (localStorage.getItem('rolename') === 'Administrator' || localStorage.getItem('role_name') === 'Administrator')
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.license_name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('driverLicense.title', 'Master Driver License')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onAddClick={handleAddClick}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      <MasterDriverLicenseAddEdit
        open={openDialog}
        id={editingId}
        companyOptions={companyOptionsNormalized}
        onClose={handleDialogClose}
        onSuccess={() => setPaginationModel((p) => ({ ...p }))}
      />

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={() => setConfirmOpen(false)}
        itemName={confirmName}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
      />
    </div>
  );
}

export default function MasterDriverLicensePage() {
  return (
    <>
      <DriverLicensePageContent />
    </>
  );
}
